/**
 * HorizontalBarChart.jsx — TM 요약 SVG 가로 막대 차트
 *
 * 결과유형별 콜 건수를 가로 막대 그래프로 표시합니다.
 * - 항목 클릭 시 드릴다운 콜 목록을 열 수 있도록 onSelect 콜백을 제공
 * - 모바일에서는 레이블·막대 너비 축소, % 칸 제거로 총 너비를 절약
 * - overflow 래퍼로 좁은 화면에서도 가로 스크롤 지원
 * dark mode: SVG 요소는 style={{ fill: 'var(--...)' }} 방식으로 CSS 변수 적용
 *
 * Props:
 *   stats            {object[]}   — [{ feedback, count }] 결과유형별 통계
 *   total            {number}     — 전체 콜 건수 (비율 계산용)
 *   selectedFeedback {string|null} — 현재 선택된 결과유형
 *   onSelect         {function}   — 항목 클릭 시 selectedFeedback 토글 핸들러
 *   isMobile         {boolean}    — 모바일 여부 (레이아웃 조정)
 */
import React from 'react';
import { BAR_COLORS } from './utils';

export default function HorizontalBarChart({ stats, total, selectedFeedback, onSelect, isMobile }) {
  const maxCount = stats.length > 0 ? Math.max(...stats.map((s) => s.count), 1) : 1;

  const ROW_H   = 36;
  const LABEL_W = isMobile ? 72 : 100;
  const BAR_W   = isMobile ? 168 : 260;
  const COUNT_W = isMobile ? 44 : 52;
  const PCT_W   = isMobile ? 0  : 44;
  const PAD_Y   = 6;

  const svgH = stats.length * ROW_H + PAD_Y * 2;
  const svgW = LABEL_W + BAR_W + COUNT_W + PCT_W + 12;

  return (
    // overflow 래퍼: 좁은 화면에서 텍스트 깨짐 방지
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <svg
        width={svgW}
        viewBox={`0 0 ${svgW} ${svgH}`}
        style={{ display: 'block', userSelect: 'none' }}
      >
        {stats.map((item, idx) => {
          const y          = PAD_Y + idx * ROW_H;
          const barLen     = (item.count / maxCount) * BAR_W;
          const pct        = total > 0 ? Math.round((item.count / total) * 100) : 0;
          const color      = BAR_COLORS[idx % BAR_COLORS.length];
          const isSelected = selectedFeedback === item.feedback;

          return (
            <g key={item.feedback} onClick={() => onSelect(item.feedback)} style={{ cursor: 'pointer' }}>
              {/* 행 배경: 선택 시 연한 파랑 */}
              <rect
                x={0} y={y} width={svgW} height={ROW_H - 2}
                fill={isSelected ? 'rgba(37,99,235,0.08)' : 'transparent'}
                rx={6}
              />
              {/* 결과유형 레이블 — dark mode: style={{ fill }} */}
              <text
                x={LABEL_W - 10} y={y + ROW_H / 2}
                textAnchor="end" dominantBaseline="middle"
                fontSize={12} fontWeight={isSelected ? 700 : 400}
                style={{ fill: isSelected ? '#2563eb' : 'var(--text-sub)' }}
              >
                {item.feedback}
              </text>
              {/* 트랙 배경 — dark mode: CSS 변수 */}
              <rect
                x={LABEL_W} y={y + (ROW_H - 16) / 2}
                width={BAR_W} height={16} rx={4}
                style={{ fill: 'var(--bg-subtle)' }}
              />
              {/* 실제 막대 */}
              <rect
                x={LABEL_W} y={y + (ROW_H - 16) / 2}
                width={barLen > 0 ? barLen : 0} height={16}
                fill={isSelected ? '#2563eb' : color}
                rx={4} opacity={isSelected ? 1 : 0.85}
              />
              {/* 건수 텍스트 — dark mode: CSS 변수 */}
              <text
                x={LABEL_W + BAR_W + 8} y={y + ROW_H / 2}
                textAnchor="start" dominantBaseline="middle"
                fontSize={12} fontWeight={600}
                style={{ fill: isSelected ? '#2563eb' : 'var(--text-sub)' }}
              >
                {item.count}건
              </text>
              {/* 비율 — 모바일에서 PCT_W=0 이므로 숨김 */}
              {PCT_W > 0 && (
                <text
                  x={LABEL_W + BAR_W + COUNT_W + 4} y={y + ROW_H / 2}
                  textAnchor="start" dominantBaseline="middle"
                  fontSize={11}
                  style={{ fill: 'var(--text-muted)' }}
                >
                  {pct}%
                </text>
              )}
              {/* 선택 강조 좌측 바 */}
              {isSelected && (
                <rect x={0} y={y + 2} width={3} height={ROW_H - 6} fill="#2563eb" rx={2} />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
